/**
 * The animals API, as the screens see it (P2-TR3).
 *
 * Money is a string on the way in and on the way out (MONEY-2); so are
 * weights, which are Decimal(6,2) in the database for the same reason — a herd
 * whose weights are floats produces a total that disagrees with itself.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { api } from '../../api/client.js';

export interface Animal {
  id: number;
  tag: string;
  species: 'goat' | 'cattle';
  breed: string | null;
  sex: 'male' | 'female';
  dobEstimate: string | null;
  /**
   * Days, not months — this is `ageDays` as `toPublicAnimal` sends it.
   *
   * It was declared here as `ageMonths` and never existed on the wire, so
   * every screen reading it got `undefined`: the herd list printed an empty
   * count and the profile showed a dash for every animal. TypeScript could not
   * catch it because the API response is cast, not parsed. Screens convert
   * with `ageMonthsFromDays`, which is the only place the two units meet.
   */
  ageDays: number | null;
  motherId: number | null;
  source: 'purchased' | 'born';
  purchaseDate: string | null;
  purchasePrice: string | null;
  tripCostAllocated: string;
  /** Set when the animal was bought on a purchase trip (P2-BR2); its price is then the trip's to correct. */
  purchaseTxnId: number | null;
  status: string;
  statusDate: string | null;
  isQurbaniTarget: boolean;
  photoUrl: string | null;
  latestWeightKg: string | null;
}

/**
 * BR-13, exactly as GET /animals/:id sends it.
 *
 * This was once declared as `healthCost` / `feedCostAllocated` / `total`, none
 * of which the server sends; `formatMoney(undefined)` then threw during render
 * and the Costs tab took the whole page down. The response is cast, not
 * parsed, so the names here are the contract — TC-P4-T33-2 pins the server side.
 */
export interface AnimalCost {
  purchasePrice: string;
  tripCostAllocated: string;
  medicalCost: string;
  /** purchase + trip + medical. */
  totalCost: string;
  /** False until feed is allocated per animal (BR-14); the total excludes it. */
  feedIncluded: boolean;
}

export interface GrowthPoint {
  date: string;
  weightKg: string;
  dayOffset: number;
  dailyGainGrams: number | null;
  expectedKg: string;
}

export interface GrowthSeries {
  animalId: number;
  species: string;
  referenceDailyGainGrams: number;
  points: GrowthPoint[];
  averageDailyGainGrams: number | null;
  onTrack: boolean | null;
  daysSinceLastWeighing: number | null;
  isStale: boolean;
}

export interface HealthEvent {
  id: number;
  eventType: string;
  date: string;
  detail: string | null;
  nextDue: string | null;
  /** The approved bill for this event; null when there is none or it is still pending (P1-BR4). */
  costAmount: string | null;
}

export interface BreedingRecord {
  id: number;
  animalId: number;
  animalTag: string;
  method: string;
  date: string;
  result: string;
  expectedDelivery: string | null;
  actualDelivery: string | null;
  offspringCount: number | null;
  daysUntilDelivery: number | null;
}

/** A breeding record as the profile embeds it — without the tag or countdown the herd-wide list adds. */
export interface ProfileBreedingRecord {
  id: number;
  method: string;
  date: string;
  result: string;
  expectedDelivery: string | null;
  actualDelivery: string | null;
  offspringCount: number | null;
}

export interface AnimalProfile extends Animal {
  cost: AnimalCost;
  mother: { id: number; tag: string } | null;
  offspring: { id: number; tag: string; dobEstimate: string | null }[];
  weights: { id: number; date: string; weightKg: string }[];
  healthEvents: HealthEvent[];
  breedingRecords: ProfileBreedingRecord[];
}

export interface AnimalFilters {
  species?: string;
  status?: string;
  sex?: string;
  page?: number;
  limit?: number;
}

interface Envelope<T> {
  success: true;
  data: T;
  meta?: { page: number; limit: number; total: number };
}

export const animalKeys = {
  list: (filters: AnimalFilters = {}) => ['animals', filters] as const,
  profile: (id: number) => ['animals', id] as const,
  growth: (id: number) => ['animals', id, 'growth'] as const,
  breeding: () => ['breeding'] as const,
  summary: () => ['animals', 'summary'] as const,
};

export function useAnimals(filters: AnimalFilters = {}): UseQueryResult<{ items: Animal[]; total: number }> {
  return useQuery({
    queryKey: animalKeys.list(filters),
    queryFn: async () => {
      const response = await api.get<Envelope<Animal[]>>('/animals', { params: filters });
      return { items: response.data.data, total: response.data.meta?.total ?? response.data.data.length };
    },
  });
}

export function useAnimalProfile(id: number): UseQueryResult<AnimalProfile> {
  return useQuery({
    queryKey: animalKeys.profile(id),
    queryFn: async () => (await api.get<Envelope<AnimalProfile>>(`/animals/${String(id)}`)).data.data,
  });
}

export function useGrowthSeries(id: number): UseQueryResult<GrowthSeries> {
  return useQuery({
    queryKey: animalKeys.growth(id),
    queryFn: async () => (await api.get<Envelope<GrowthSeries>>(`/animals/${String(id)}/growth`)).data.data,
  });
}

export function useUpcomingDeliveries(): UseQueryResult<BreedingRecord[]> {
  return useQuery({
    queryKey: animalKeys.breeding(),
    queryFn: async () =>
      (await api.get<Envelope<BreedingRecord[]>>('/breeding', { params: { upcoming: 'true' } })).data.data,
  });
}

/**
 * Registering an animal, matching `createAnimalSchema` on the server.
 *
 * The names are the server's. Its schema is `.strict()`, so an invented key —
 * `ageMonths`, say, or `dob` — is a 422 rather than a field quietly ignored.
 * Age is converted to `dobEstimate` before it gets here; nothing downstream of
 * this type knows that a partner typed months.
 *
 * `status` is absent on purpose and must stay absent (SEC-4): a new animal is
 * active because the server says so, not because a form asked.
 */
export interface CreateAnimalInput {
  species: 'goat' | 'cattle';
  sex: 'male' | 'female';
  source: 'purchased' | 'born';
  /** Omitted to take the next suggested tag (P2-BR1). */
  tag?: string;
  breed?: string;
  /** Derived from the age in months; never sent as an age. */
  dobEstimate?: string;
  /** BR-12 — a farm-born animal links to its mother. */
  motherId?: number;
  purchaseDate?: string;
  purchasePrice?: string;
  photoUrl?: string;
}

/**
 * Registers an animal (BR-12, P2-BR3).
 *
 * `POST /animals` has existed since P2-T01 with nothing in the client calling
 * it: the only route into the herd was the purchase-trip wizard, so a calf born
 * on the farm could not be recorded at all and no animal could be given an age
 * or a mother.
 *
 * Invalidates the herd and its summary counts, but no money key — registering
 * an animal here posts no transaction. A purchase that moved cash is the trip
 * wizard's job, which is why this form's purchase price is recorded on the
 * animal without an account.
 */
export function useCreateAnimal(): UseMutationResult<Animal, unknown, CreateAnimalInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateAnimalInput) => (await api.post<Envelope<Animal>>('/animals', input)).data.data,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['animals'] }),
        queryClient.invalidateQueries({ queryKey: animalKeys.summary() }),
      ]);
    },
  });
}

/**
 * The next free tag for a species, so the form can offer it (P2-BR1).
 *
 * `/next-tag`, not `/suggest-tag` — the route is named for what it returns,
 * and it is declared before `/:id` on the server so the word is never read as
 * an animal id.
 */
/**
 * P4-T34 — correcting a registration. Only changed fields are sent; the server
 * re-checks each one the way registration does.
 */
export interface AnimalChanges {
  tag?: string;
  sex?: 'male' | 'female';
  breed?: string;
  dobEstimate?: string;
  /** Null clears a mother entered by mistake. */
  motherId?: number | null;
  purchaseDate?: string;
  purchasePrice?: string;
  photoUrl?: string;
}

export function useUpdateAnimal(): UseMutationResult<Animal, unknown, { id: number; changes: AnimalChanges }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, changes }) =>
      (await api.patch<Envelope<Animal>>(`/animals/${String(id)}`, changes)).data.data,
    onSuccess: async () => {
      // The profile, the herd list and every cost built on the price.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['animals'] }),
        queryClient.invalidateQueries({ queryKey: animalKeys.summary() }),
      ]);
    },
  });
}

export function useNextTag(species: 'goat' | 'cattle'): UseQueryResult<{ tag: string }> {
  return useQuery({
    queryKey: ['animals', 'next-tag', species],
    queryFn: async () =>
      (await api.get<Envelope<{ tag: string }>>('/animals/next-tag', { params: { species } })).data.data,
  });
}

export interface AnimalSaleInput {
  animalId: number;
  date: string;
  /** A string all the way to the wire (MONEY-1, MONEY-2). */
  salePrice: string;
  /**
   * Where the money went.
   *
   * Omitted only for a credit sale (BR-24, D-019), which names the buyer who
   * owes instead — an animal sold for nothing traceable, to nobody in
   * particular, is the discrepancy the ledger exists to prevent.
   */
  accountId?: number;
  customerId?: number;
  /** Sale weight, for the price per kilo the partners negotiate on. */
  weightKg?: string;
  note?: string;
}

/**
 * Sells an animal (P3-BR1, BR-15).
 *
 * The endpoint has existed since P3 with nothing calling it, so a cow could be
 * bought, reared, fed, treated and costed in the app and never sold.
 *
 * It does two things at once: the animal leaves the herd, and a *pending*
 * income entry is created (BR-07). The animal leaves on the strength of the
 * record rather than the approval, which is why rejecting or voiding that entry
 * puts it back (P3-BR1) — so this invalidates the herd as well as the money.
 */
export function useRecordAnimalSale(): UseMutationResult<unknown, unknown, AnimalSaleInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AnimalSaleInput) => (await api.post('/sales/animal', input)).data,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['animals'] }),
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'finance'] }),
      ]);
    },
  });
}

export interface TripAnimalInput {
  species: string;
  sex: string;
  purchasePrice: string;
  breed?: string;
  dobEstimate?: string;
}

/**
 * The purchase-trip payload, matching `purchaseTripSchema` on the server.
 *
 * The names are the server's, not convenient local ones. This previously sent
 * `purchaseDate` and `costs`, which the server's `.strict()` schema rejected as
 * unrecognised keys — so the wizard could never save a trip at all. Component
 * tests mocked the API and so never compared the two shapes.
 */
export interface TripInput {
  /** `date`, not `purchaseDate`. */
  date: string;
  /** Required: BR-05 says a purchase names the account it was paid from. */
  accountId: number;
  vendorId?: number;
  /** Which category the animal purchases themselves land in. */
  animalCategoryId?: number;
  animals: TripAnimalInput[];
  /** `tripCosts`, not `costs`, and `categoryId` is required on each. */
  tripCosts: { label: string; amount: string; categoryId: number }[];
}

export interface TripResult {
  animals: Animal[];
  animalsTotal: string;
  tripCostTotal: string;
  grandTotal: string;
  transactionIds: number[];
  allocation: { tag: string; purchasePrice: string; tripShare: string }[];
}

export function useRecordTrip(): UseMutationResult<TripResult, unknown, TripInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: TripInput) =>
      (await api.post<Envelope<TripResult>>('/animals/purchase-trip', input)).data.data,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['animals'] }),
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
      ]);
    },
  });
}

export function useRecordWeight(): UseMutationResult<
  unknown,
  unknown,
  { animalId: number; date: string; weightKg: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ animalId, ...body }) => (await api.post(`/animals/${String(animalId)}/weights`, body)).data,
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({ queryKey: animalKeys.profile(variables.animalId) });
      await queryClient.invalidateQueries({ queryKey: animalKeys.growth(variables.animalId) });
    },
  });
}
